import isString from 'lodash/isString';
import isBoolean from 'lodash/isBoolean';
import isUndefined from 'lodash/isUndefined';
import type { SearchResult } from '../search/interfaces';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { ItemAccessControl } from './itemAccessControlDto';
import type { FolderSearchResultHit } from './searchResultHit';
import type { ItemMetaData } from './fieldValue';
import type { CompleteType } from '../util/general';
import type { MetadataDeltaPayload } from './mdfModels';

export const ArchiveFolderName = 'Archive';
export const FolderTypeName = 'folder';

export enum FolderKind {
  TenantRoot = 'tenant-root',
  ArchiveRoot = 'archive-root',
}

export interface ItemPaths {
  paths: Record<string, FolderPathSegmentDto[][]>;
}

/*
  Response consists of dictionary of item ancestry information indexed by item ID.

  The ancestry information of a single item consists of dictionary of item paths indexed by parent folder id.

  The paths for each combination of item and parent folder consists of an array of paths.

  Each path consists of an array of FolderPathSegmentDto objects.

*/
export type AncestryResponseDto = Record<string, ItemPaths>;

/**
 * Represents the view model for a folder, specifically used by the Mimir client.
 */
export interface FolderViewModel extends FolderDto {
  subTrees?: FolderViewModel[];
  loading: boolean;
  hasLoadedSubFolders: boolean;
  isShortcut?: boolean;
  isHovered?: boolean;
  /**
   * Contains an array of folder path segments that can be used to determine folder paths.
   * For example, 'workFolder' path in the ui.
   */
  folderPaths?: FolderPathSegmentDto[];
  /**
   * `true` if any child folder is currently being dragged for reordering.
   */
  hasDraggedChildren?: boolean;
}

export interface FolderDto {
  id: string;
  owner: string;
  name: string;
  createdOn: string;
  hasSubfolder: boolean;
  kind?: FolderKind;
  parent?: string;
  itemType: 'folder';
  visibleTo?: ItemAccessControl;
  updatableBy?: ItemAccessControl;
  folderState?: FolderState;
  purgeDate?: string;
  deletedBy?: string;
  metadata?: ItemMetaData;
  /**
   * The lexical order of the children in the folder in which they should be displayed.
   * if undefined or null, the default alphabetical order will be applied.
   */
  sortKey?: string | null;
}

export interface FolderWsDto {
  id: string;
  owner: string;
  name: string;
  createdOn: string;
  hasSubfolder: boolean;
  kind?: FolderKind;
  parent?: string;
  itemType: 'folder';
  visibleTo?: ItemAccessControl;
  updatableBy?: ItemAccessControl;
  folderState?: FolderState;
  purgeDate?: string;
  deletedBy?: string;
  metadata: undefined;
  /**
   * The lexical order of the children in the folder in which they should be displayed.
   * if undefined or null, the default alphabetical order will be applied.
   */
  sortKey?: string | null;
}

export function isFolderDto(maybe: unknown): maybe is FolderDto {
  if (!isObject(maybe)) return false;
  const f = maybe as Partial<FolderDto>;
  return !!(f.id && f.owner && f.name && f.itemType === 'folder');
}

export type UpdateFolderDto = {
  visibleTo?: ItemAccessControl,
  updatableBy?: ItemAccessControl,

  // Note: Only one of name and parentId can currently be specified in the same request
  name?: string,
  parentId?: string,

  /**
   * The lexical order of the children in the folder in which they should be displayed.
   * if undefined or null, the default alphabetical order will be applied.
   */
  sortKey?: string | null,

  /**
   * Change the folder's owner. Requires folder/changeOwner permission.
   */
  owner?: string,
};

export type UpdateFolderAccessControlDto = Pick<UpdateFolderDto, 'updatableBy' | 'visibleTo'>;

export interface AccessControls {
  visibleTo: ItemAccessControl | undefined;
  updatableBy: ItemAccessControl | undefined;
}

/**
 * A folder update that does not involving moving the folder to a new parent.
 */
export interface SimpleFolderUpdateInput {
  visibleTo?: ItemAccessControl;
  updatableBy?: ItemAccessControl;

  // Note: Only one of name and parentId can currently be specified in the same request
  name?: string;

  /**
   * The lexical order of the children in the folder in which they should be displayed.
   * if undefined or null, the default alphabetical order will be applied.
   */
  sortKey?: string | null;

  /**
   * Change the folder's owner. Requires folder/changeOwner permission.
   */
  owner?: string;
}


export interface AddToFolderDto {
  /**
   * Item id to move
   */
  id: string;

  /**
   * Current parentId - if provided & present as part of the items' folderParents,
   * the operation constitutes as a MOVE rather than ADD, and the provided folder id will be
   * removed from the items' folderParents.
   */
  parentId?: string;
}

export enum FolderState {
  DELETING = 'deleting',
}

/**
 * This represents one segment in a path.
 * A full path from the root folder in an array of such
 * elements, where the element at index 0 is the root.
 */
export interface FolderPathSegmentDto {
  // The ID of the folder
  id: string;
  // The human-readable name of the folder
  name: string;
}

export const validateFolderName = (name: string): boolean => {
  if (!name) {
    return false;
  }

  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return false;
  }

  if (trimmed === '.' || trimmed === '..') {
    return false;
  }

  // Simplified - expand as necessary with a regex
  if (trimmed.includes('?') || trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('*')) {
    return false;
  }

  return true;
};

export const isAddToFolderDto = (obj: unknown): obj is AddToFolderDto => {
  if (!isObject(obj)) return false;
  const probablyAddToFolderDto = obj as Partial<AddToFolderDto>;
  return probablyAddToFolderDto.id !== undefined;
};

export interface FolderUniqueNameTableEntity {
  parent: string;
  name: string;
  id: string;
}

export interface FolderPathCreationDto {
  path: string;
  rootFolderId?: string;
}

export interface GetFolderContentQueryParams extends GetFolderContentScrollQueryParams {
  type?: typeof FolderTypeName;
  from?: string | number;
  itemsPerPage?: string | number;
  searchString?: string;
  showOnlyDeletedItems?: string | boolean;
}

export interface GetFolderContentScrollQueryParams {
  fetchAllRecursively?: string | boolean;
  mScrollId?: string;
  skipDeleted?: boolean;
  skipFolders?: boolean;
}

export type GetFolderContentResponseDto = FolderSearchResultHit[] | FolderDto[] | SearchResult;

export enum FolderWebhook {
  FOLDER_CREATE = 'FolderCreate',
  FOLDER_CHANGE = 'FolderChange',
  FOLDER_DELETE = 'FolderDelete',
  FOLDER_METADATA_UPDATE = 'FolderMetadataUpdate',
}

export type FolderChangeEventType = 'renamed' | 'moved' | 'owner_changed' | 'all';

export type FolderChangeWebhookParams = {
  oldName: string;
  oldParent: string | undefined;
  oldOwner: string | undefined;
};

export interface FolderWebhookDto {
  event: FolderWebhook,
  changeType?: FolderChangeEventType,
  folder: Pick<FolderDto, 'id' | 'name' | 'parent' | 'createdOn'> & {
    owner?: string,
    folderPath?: string,
    deletedBy?: string,
    metadata?: ItemMetaData,
  }
}

export function isFolderCreateWebhookDto(dto: any): dto is FolderWebhookDto {
  return dto?.event === FolderWebhook.FOLDER_CREATE && !!dto?.folder?.id;
}

export function isFolderDeleteWebhookDto(dto: any): dto is FolderWebhookDto {
  return dto?.event === FolderWebhook.FOLDER_DELETE && !!dto?.folder?.id;
}

export function isFolderMetadataUpdateWebhookDto(dto: any): dto is FolderWebhookDto {
  return dto?.event === FolderWebhook.FOLDER_METADATA_UPDATE && !!dto?.folder?.id;
}

export function isInstanceOfFolderWsDto(dto: unknown): dto is FolderWsDto {
  if (!isObject(dto)) return false;
  const possibleDto = dto as Partial<FolderWsDto>;

  return isString(possibleDto.id)
    && isString(possibleDto.name)
    && isString(possibleDto.owner)
    && isBoolean(possibleDto.hasSubfolder)
    && (isString(possibleDto.parent) || isUndefined(possibleDto.parent))
    && possibleDto.itemType === 'folder';
}

export interface FolderNodeBase {
  id: string;

  /**
   * The folder owner property is either UUID identifier of a user or is equal
   * to the tenantId.
   *
   * For regular folders, it can be either of these.
   *
   * For folders with a kind property (FolderKind), the owner is always equal to
   * the tenantId.
   */
  owner: string;
}

export interface SlimFolderNode extends FolderNodeBase {
  name: string;
  parent?: string;
  visibleTo?: ItemAccessControl;
  updatableBy?: ItemAccessControl
}

export interface SlimFolderNodeWithPurgeDate extends SlimFolderNode {
  purgeDate?: string;
}

export interface FolderNode extends SlimFolderNode {
  tenantId: string;
  createdOn: string;
  kind?: FolderKind;
  parent?: string;
  isHidden?: boolean;
  folderState?: FolderState;
  purgeDate?: string;
  deletedBy?: string;
  pkRoot?: string;
  skBranch?: string;
  modifiedOn?: string;
  indexTime?: number;
  metadata?: ItemMetaData;
  /**
   * The lexical order of the children in the folder in which they should be displayed.
   * if undefined or null, the default alphabetical order will be applied.
   */
  sortKey?: string | null;
}

// Only used for replication
export type StrippedFolderEntity = Pick<
  FolderNode,
  'id' |
  'name' |
  'tenantId' |
  'owner' |
  'createdOn' |
  'kind' |
  'parent' |
  'isHidden' |
  'visibleTo' |
  'updatableBy' |
  'folderState' |
  'purgeDate' |
  'deletedBy' |
  'modifiedOn' |
  'metadata' |
  'sortKey'
>;

export function stripFolder(folderNode: FolderNode): StrippedFolderEntity {
  const stripped: CompleteType<StrippedFolderEntity> = {
    id: folderNode.id,
    name: folderNode.name,
    tenantId: folderNode.tenantId,
    owner: folderNode.owner,
    createdOn: folderNode.createdOn,
    kind: folderNode.kind,
    parent: folderNode.parent,
    isHidden: folderNode.isHidden,
    visibleTo: folderNode.visibleTo,
    updatableBy: folderNode.updatableBy,
    folderState: folderNode.folderState,
    purgeDate: folderNode.purgeDate,
    deletedBy: folderNode.deletedBy,
    modifiedOn: folderNode.modifiedOn,
    metadata: folderNode.metadata,
    sortKey: folderNode.sortKey,
  };
  return stripped;
}

export interface FolderMetadataDto {
  id: string, // Folder ID
  metadata: ItemMetaData,
}

export interface FolderMetadataUpdatePayload {
  metadataDelta: Partial<MetadataDeltaPayload>,
}

export const folderUpdateKeys = ['name', 'parentId', 'visibleTo', 'updatableBy', 'sortKey', 'owner'] as const;
export type FolderUpdateKey = typeof folderUpdateKeys[number];
